import React, { useMemo, useState, useCallback, lazy, Suspense } from 'react';
import cn from 'classnames';
import Slider from 'components/UI/Slider';
import { CustomDot, CustomPagination } from 'components/UI/Slider/utils';
import VideoCard from './VideoCard';
import { useStateModal } from 'utils/hooks';
import { useInsightsVideo } from 'staticQueries/insights-video';

import * as styles from './InsightsVideo.module.scss';

const PlayerModal = lazy(() => import('components/Modals/PlayerModal'));

export default function InsightsVideo({ theme, videoIdArray }) {
  const videos = useInsightsVideo(videoIdArray);
  const { modalState, showModal, hideModal } = useStateModal();
  const [current, setCurrent] = useState(0);
  const [videoSrc, setVideoSrc] = useState(null);
  const [fadeInAnimation, setFadeInAnimation] = useState(false);
  const isThemeDark = theme === 'dark';

  const openModal = useCallback(videoId => {
    setVideoSrc(videoId);
    showModal();
  }, []);

  const setCurrentKey = useCallback((prev, next) => {
    setCurrent(next);
  }, []);

  const onAnimationEnd = () => {
    setFadeInAnimation(false);
  };

  const settings = useMemo(
    () => ({
      customPaging: CustomDot.bind(null, videos.length, current),
      appendDots: CustomPagination,
      dotsClass: 'slick-pagination',
      dots: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: true,
      infinite: true,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            arrows: false,
          },
        },
        {
          breakpoint: 544,
          settings: {
            adaptiveHeight: true,
            arrows: false,
          },
        },
      ],
    }),
    [videos.length, current],
  );

  return (
    <>
      <div
        className={cn(
          styles.slider,
          isThemeDark ? 'defaultSliderDarkBg' : 'defaultSlider',
          isThemeDark && styles.dark,
          fadeInAnimation ? styles.fadeInAnimation : undefined,
        )}
        onAnimationEnd={onAnimationEnd}
      >
        <Slider {...settings} beforeChange={setCurrentKey}>
          {videos.map(item => {
            return (
              <div key={item.videoId} className={styles.slide}>
                <VideoCard
                  {...item}
                  cover={item.cover}
                  openModal={() => openModal(item.videoId)}
                  isThemeDark={isThemeDark}
                />
              </div>
            );
          })}
        </Slider>
      </div>
      <Suspense fallback={null}>
        <PlayerModal
          isOpen={modalState.isOpen}
          closeModalCallback={hideModal}
          data={{ videoSrc }}
        />
      </Suspense>
    </>
  );
}
